import type { BaselineSnapshot } from '../types';

interface BaselineSummaryStepProps {
  transcription?: BaselineSnapshot;
  composition?: BaselineSnapshot;
  onContinue: () => void;
  continueLabel?: string;
}

export default function BaselineSummaryStep({
  transcription,
  composition,
  onContinue,
  continueLabel = 'Vælg dit mål',
}: BaselineSummaryStepProps) {
  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6">
      <header className="text-center">
        <p className="label">Dit udgangspunkt</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Sådan skriver du i dag
        </h1>
        <p className="mt-2 text-sm text-muted">
          De fleste starter under deres drømmemål. Målet med træningen er at komme
          over dit udgangspunkt — især ved afskrift fra skærmen.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        <ResultCard
          title="Afskrift"
          subtitle="Tekst på skærmen — det vi følger i træningen"
          snapshot={transcription}
          accuracyLabel="Nøjagtighed"
          emptyText="Ikke målt"
        />
        <ResultCard
          title="Hverdag"
          subtitle="Fri skrivning — det du gør mest"
          snapshot={composition}
          accuracyLabel="Flyd"
          emptyText="Sprunget over"
        />
      </div>

      {transcription && composition && transcription.wpm > composition.wpm && (
        <p className="text-center text-xs text-muted">
          Du skrev hurtigere ved afskrift end ved fri skrivning denne gang — det
          sker. Begge tal gemmes som reference.
        </p>
      )}
      {transcription && composition && composition.wpm > transcription.wpm && (
        <p className="text-center text-xs text-muted">
          Du skrev hurtigere ved fri skrivning — typisk når blikket kan blive på
          tastaturet. Træningen fokuserer på afskrift fra skærmen.
        </p>
      )}

      <button
        type="button"
        onClick={onContinue}
        className="w-full rounded-xl bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400"
      >
        {continueLabel}
      </button>
    </div>
  );
}

function ResultCard({
  title,
  subtitle,
  snapshot,
  accuracyLabel,
  emptyText,
}: {
  title: string;
  subtitle: string;
  snapshot?: BaselineSnapshot;
  accuracyLabel: string;
  emptyText: string;
}) {
  return (
    <div className="surface p-4 text-center">
      <p className="text-sm font-semibold text-strong">{title}</p>
      <p className="mt-0.5 text-xs text-faint">{subtitle}</p>
      {snapshot ? (
        <>
          <p className="mt-3 font-mono text-3xl font-semibold text-strong">
            {snapshot.wpm.toFixed(0)}{' '}
            <span className="text-lg font-normal text-muted">wpm</span>
          </p>
          <p className="mt-1 text-sm text-muted">
            {accuracyLabel}: {(snapshot.accuracy * 100).toFixed(0)}%
          </p>
        </>
      ) : (
        <p className="mt-3 text-sm text-muted">{emptyText}</p>
      )}
    </div>
  );
}
